<script setup lang="ts">
import type { Meta, Link, Script } from '@unhead/vue'
import { CLS_VERSION } from './plugins/00.aa-dev-cls'

const lang = useLocale()
const fast = useFast()
const mobile = useMobile()
const route = useRoute()
const { toFullUrl, rewrite } = useLinks()

const enUS = 'en_US'
const ltLT = 'lt_LT'

/**
 * Canonical hygiene: strip tracking params before building canonical /
 * og:url / hreflang. Without this, a crawled `?gclid=...` on any page not
 * covered by useSeoSearch/useSeoProduct (homepage, info, faq) would
 * self-canonicalize to the tracking url.
 */
const TRACKING_PARAMS = new Set(['gclid', 'fbclid', 'msclkid', 'ttclid', 'yclid', 'gbraid', 'wbraid', 'mc_cid', 'mc_eid', 'igshid', 'srsltid', 'ref'])
const isTrackingParam = (key: string) => key.startsWith('utm_') || TRACKING_PARAMS.has(key)

const cleanRoute = computed(() => {
  const query: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(route.query)) {
    if (!isTrackingParam(key)) query[key] = value
  }
  return { name: route.name ?? undefined, params: route.params, query } as Parameters<typeof rewrite>[0]
})

const cleanFullUrl = computed(() => toFullUrl(rewrite(cleanRoute.value, lang.value)))

const cls = computed(() => `cls${CLS_VERSION}`)

useHead({
  htmlAttrs: toRef(() => ({ dir: 'ltr', lang })),
  bodyAttrs: toRef(() => ({ class: [mobile.value ? 'm' : 'd', cls.value] })),
  titleTemplate: (i) => {
    const t = i?.length ? `${i} | ${SEO_NAME}` : SEO_NAME
    if (import.meta.dev) return `[dev] ${t}`
    return t
  },
  link: () => {
    function linkLang(): Link[] {
      const urlLt = toFullUrl(rewrite(cleanRoute.value, locales.lt))
      const urlEn = toFullUrl(rewrite(cleanRoute.value, locales.en))

      const links: Link[] = [{ key: 'canonical', rel: 'canonical', href: cleanFullUrl.value }]

      if (urlLt !== urlEn) {
        links.push(
          { key: `alternate${locales.lt}`, rel: 'alternate', href: urlLt, hreflang: locales.lt },
          { key: `alternate${locales.en}`, rel: 'alternate', href: urlEn, hreflang: locales.en },
          { key: 'alternatedefault', rel: 'alternate', href: urlEn, hreflang: 'x-default' }
        )
      }

      return links
    }

    return [...linkLang()]
  },
  meta: () => {
    const metaLang = (): Meta[] => {
      const content = cleanFullUrl.value
      const lt = lang.value === 'lt'
      return [
        { name: 'twitter:url', content },
        { property: 'og:url', content },
        { property: 'og:locale', content: lt ? ltLT : enUS },
        { property: 'og:locale:alternate', content: lt ? enUS : ltLT },
      ]
    }

    return [
      //
      ...metaLang(),
      ...seoMetaDescription(lang.value === 'lt' ? SEO_DESCRIPTION_LT : SEO_DESCRIPTION_EN),
    ]
  },
})

if (import.meta.server) {
  useHead({
    script: () => {
      const res: Script[] = []
      if (!fast.value) {
        res.push({
          type: 'text/javascript',
          innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer','GTM-TD4DJ2S4');`,
        })
      }

      const seo = seoSchema([
        {
          potentialAction: schemaSearchAction(),
          '@type': 'WebSite',
          '@id': seoWebsiteId,
          url: SEO_URL,
          name: SEO_NAME,
          inLanguage: [locales.lt, locales.en],
          publisher: { '@id': seoOrgId },
        },
        schemaOrgStub(),
      ])
      if (seo) res.push(seo)

      return res
    },
    link: () => [
      { rel: 'preconnect', href: 'https://img.linenbylinas.com' },
      { rel: 'preconnect', href: 'https://media.linenbylinas.com' },
      { rel: 'dns-prefetch', href: 'https://img.linenbylinas.com' },
      { rel: 'dns-prefetch', href: 'https://media.linenbylinas.com' },
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', type: 'image/png', href: '/favicon.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png?v=2' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png?v=2' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png?v=2' },
      { rel: 'manifest', href: '/site.webmanifest?v=2' },
      { rel: 'mask-icon', href: '/safari-pinned-tab.svg?v=2', color: '#002855' },
      { rel: 'shortcut icon', href: '/favicon.ico?v=2' },
    ],
    meta: () => [
      { name: 'p:domain_verify', content: '78ee793adb8cf3113bac6f9fbe357a76' }, // Pinterest
      { name: 'facebook-domain-verification', content: '25n9qce097683ivyez1cjwjztlt3sp' },
      { name: 'viewport', content: 'width=device-width,initial-scale=1,shrink-to-fit=no' },
      { name: 'msapplication-TileColor', content: '#da532c' },
      { property: 'og:site_name', content: SEO_NAME },
      { property: 'og:type', content: 'website' },
      { name: 'theme-color', content: '#fdfdfe' },
      ...seoMetaTitle(SEO_NAME),
      ...seoMetaImage({ url: SEO_LOGO, width: 316, height: 316 }, 'image/png', 'summary'),
    ],
  })
}

const inited = useInited('auth')
</script>

<template>
  <NuxtLayout>
    <NuxtPage />
    <LazyAppInit v-if="inited" key="init" />
  </NuxtLayout>
</template>
